import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { useTheme } from "../theme";
import { Trophy, LogOut, Menu, X, Sun, Moon } from "lucide-react";
import { useState } from "react";

const LanguageSwitcher = () => {
  const { lang, setLang, langs } = useI18n();
  return (
    <div
      className="inline-flex items-center gap-1 rounded-sm border border-white/10 bg-white/[0.03] backdrop-blur-xl p-0.5"
      data-testid="lang-switcher"
    >
      {langs.map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`px-2 py-1 text-[11px] uppercase tracking-[0.18em] font-bold transition-colors ${
            lang === l
              ? "bg-white text-black"
              : "text-white/60 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const navItemClass = ({ isActive }) =>
    `text-xs uppercase tracking-[0.2em] font-head transition-colors ${
      isActive ? "text-[#00D37F]" : "text-white/50 hover:text-white"
    }`;

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/10 bg-black/60 backdrop-blur-xl"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="relative w-7 h-7 flex items-center justify-center bg-[#00D37F] text-black group-hover:bg-[#00E78B] transition-colors">
            <Trophy className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <span className="font-display text-2xl leading-none">
            POLLAGOL<span className="text-[#00D37F]">26</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/matches" className={navItemClass} data-testid="nav-matches">
            {t("nav_matches")}
          </NavLink>
          <NavLink to="/leaderboard" className={navItemClass} data-testid="nav-leaderboard">
            {t("nav_leaderboard")}
          </NavLink>
          <NavLink to="/prizes" className={navItemClass} data-testid="nav-prizes">
            {t("nav_prizes")}
          </NavLink>
          {user && (
            <NavLink to="/logs" className={navItemClass} data-testid="nav-logs">
              {t("nav_logs")}
            </NavLink>
          )}
          {user && (user.role === "admin" || user.role === "subadmin") && (
            <NavLink to="/admin" className={navItemClass} data-testid="nav-admin">
              {t("nav_admin")}
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            className="h-8 w-8 inline-flex items-center justify-center border border-white/10 bg-white/[0.03] hover:bg-white/10 text-white/70 hover:text-white transition-colors"
          >
            {theme === "dark" ? (
              <Sun className="w-3.5 h-3.5" />
            ) : (
              <Moon className="w-3.5 h-3.5" />
            )}
          </button>
          <div className="hidden sm:flex items-center gap-2">
            {user ? (
              <>
                <div
                  className="hidden md:flex items-center gap-2 px-3 h-9 border border-[#00D37F]/30 bg-[#00D37F]/10"
                  data-testid="nav-balance"
                >
                  <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/60 font-bold">
                    {t("bal_short")}
                  </span>
                  <span className="font-mono text-sm font-bold text-[#00D37F] tabular-nums">
                    {user.points ?? 0}
                  </span>
                </div>
                <span className="text-xs uppercase tracking-wider text-white/70 font-head hidden md:inline">
                  @{user.username}
                </span>
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="inline-flex items-center gap-1 h-9 px-3 bg-white/5 hover:bg-white/10 border border-white/10 text-xs uppercase tracking-wider font-head transition-colors"
                >
                  <LogOut className="w-3.5 h-3.5" />
                  {t("logout")}
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="login-link"
                  className="h-9 px-3 inline-flex items-center text-xs uppercase tracking-wider font-head text-white/70 hover:text-[#00D37F]"
                >
                  {t("login")}
                </Link>
                <Link
                  to="/register"
                  data-testid="register-link"
                  className="h-9 px-4 inline-flex items-center text-xs uppercase tracking-wider font-head bg-[#00D37F] hover:bg-[#00E78B] text-black transition-colors"
                >
                  {t("register")}
                </Link>
              </>
            )}
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="md:hidden text-white p-1"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-black/90 backdrop-blur-xl">
          <div className="px-4 py-3 flex flex-col gap-3">
            <NavLink to="/matches" onClick={() => setOpen(false)} className={navItemClass}>
              {t("nav_matches")}
            </NavLink>
            <NavLink to="/leaderboard" onClick={() => setOpen(false)} className={navItemClass}>
              {t("nav_leaderboard")}
            </NavLink>
            <NavLink to="/prizes" onClick={() => setOpen(false)} className={navItemClass}>
              {t("nav_prizes")}
            </NavLink>
            {user && (
              <NavLink to="/logs" onClick={() => setOpen(false)} className={navItemClass}>
                {t("nav_logs")}
              </NavLink>
            )}
            {user && (user.role === "admin" || user.role === "subadmin") && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className={navItemClass}>
                {t("nav_admin")}
              </NavLink>
            )}
            <div className="pt-2 border-t border-white/10 flex items-center gap-2">
              {user ? (
                <button
                  onClick={() => {
                    setOpen(false);
                    handleLogout();
                  }}
                  className="h-9 px-3 bg-white/5 border border-white/10 text-xs uppercase tracking-wider font-bold"
                >
                  {t("logout")}
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="h-9 px-3 inline-flex items-center text-xs uppercase tracking-wider font-bold text-white/70"
                  >
                    {t("login")}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="h-9 px-4 inline-flex items-center text-xs uppercase tracking-wider font-bold bg-[#007AFF] text-white"
                  >
                    {t("register")}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
